/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { forwardRef, useEffect, useRef, type Ref, type RefObject } from "react";
import "./FileManagerLeftSideTree.css";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { Formatters } from "slickgrid-react";
import useFileManagerLeftSideTree from "./useFileManagerLeftSideTree";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import { renderToStaticMarkup } from "react-dom/server";
import { t } from "@i18n";
import type {
  ColumnDefinition,
  GridClickEvent,
  GridRowBase,
  SlickGrid1Ref,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import type { FileManagerData, FileManagerRow } from "./fileSystemServer.schema";

/** One node of the folder tree: a file-manager row plus the grid's own row markers. */
type FolderTreeRow = GridRowBase & { name?: string };

/** Method shorthand on the callbacks — see the note in `useFileManagerShared`. */
interface LeftSideTreeProps {
  setSelectedFolder(folder: FileManagerRow): void;
  selectedFolder: FileManagerRow;
  fileManagerData: FileManagerData;
  onFileLoad(files: FileManagerRow[]): void;
}

const LeftSideTree = forwardRef(
  (
    { setSelectedFolder, selectedFolder, fileManagerData, onFileLoad }: LeftSideTreeProps,
    ref: Ref<SlickGrid1Ref<FolderTreeRow>>,
  ) => {
    const tempRef = useRef<SlickGrid1Ref<FolderTreeRow>>(null);
    const treeViewRef = (ref || tempRef) as RefObject<SlickGrid1Ref<FolderTreeRow> | null>;

    const {
      // State
      folderTreeData,

      // Handlers
      handleOnClick,
      titleFormatter,
      collapseParentNodesWithoutChildData,
      syncSelectedFolder,
    } = useFileManagerLeftSideTree({
      setSelectedFolder,
      selectedFolder,
      fileManagerData,
      onFileLoad,
    });

    // Sync selected folder
    useEffect(() => {
      syncSelectedFolder(treeViewRef);
    }, [selectedFolder.id, folderTreeData]);

    const getTreeView = () => (
      <SlickGrid1
        ref={treeViewRef}
        gridOptions={{
          enableCheckboxSelector: false,
          enableContextMenu: false,
          forceFitColumns: true,
        }}
        titleFormatter={titleFormatter}
        selectCheckBoxOnRowClick={true}
        disableTreeCollapseOnDataChange={true}
        enableMultiSelect={false}
        surpassedDataset={folderTreeData}
        // enableFilteringOption
        enablePointerForRows
        onClick={(args: GridClickEvent) => handleOnClick(args, treeViewRef)}
        disableRowBorder={true}
        disableColumnBorder={true}
        disableOuterBorder={true}
        hasChildMapping="hasChild"
        parentIdMapping="parentId"
        treeGrid={true}
        setGridReady={() => collapseParentNodesWithoutChildData(treeViewRef)}
        disableChildDataFetch
        initialSortColumnId="name"
        columnDefinitions={
          [
            {
              id: "name",
              name: "Directory",
              field: "name",
              sortable: true,
              resizable: true,
              filterType: "Text",
              headerRenderer: () => renderToStaticMarkup(<HeaderWithIcon icon="directory" label={t("Directory")} />),
              // `Formatters.tree` is a slickgrid-universal Formatter; ColumnDefinition declares the
              // narrower row-typed formatter, so the brand/formatter gap is bridged once here.
              formatter: Formatters.tree as ColumnDefinition<FolderTreeRow, "name">["formatter"],
              minWidth: 300,
            },
          ] as ValidatedColumnDefinition<FolderTreeRow>[]
        }
        gridHeight="100%"
      />
    );

    return <div className="w-full overflow-auto flex h-full">{getTreeView()}</div>;
  },
);

LeftSideTree.displayName = "LeftSideTree";
export default LeftSideTree;
